import "./App.css";
import { BrowserRouter as Router, Navigate, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import { ToastContainer, cssTransition } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./toast-theme.css";
import MembershipHome from "./MembershipHome";
import ScrollLockManager from "./ScrollLockManager";
import MembershipLogin from "./MembershipLogin";
import CourseReviewForm from "./CourseReviewForm";
import MembershipSsoLogin from "./MembershipSsoLogin";
import ResetPassword from "./ResetPassword";
import CourseLandingPage from "./courselanding/CourseLandingPage";
import CheckoutPage from "./courselanding/checkout/CheckoutPage";
import CheckoutPreview from "./courselanding/checkout/CheckoutPreview";
import ThankYouPage from "./courselanding/checkout/ThankYouPage";
import ThankYouPreview from "./courselanding/checkout/ThankYouPreview";
import NotFound from "./NotFound";
import { SECTION_SLUGS, START_PAGE } from "./nav";
import { hasSession } from "./memberSession";

/* Slide + settle instead of toastify's default bounce. The keyframes live in
   toast-theme.css, which swaps them for a slide-UP variant at the bottom
   placement, so this stays one declaration for both. */
const ToastMotion = cssTransition({
  enter: "toast-in",
  exit: "toast-out",
  collapseDuration: 200,
});


/**
 * Send a signed-out visitor to the membership's login page, remembering where
 * they were going so signing in lands them there (MembershipLogin reads ?next
 * and ?ref).
 *
 * PUBLIC DOORWAYS ARE EXEMPT. A group landing page (?gid) and a referral invite
 * (?ref) exist precisely so someone who is not a member yet can look at a group
 * and join it — GroupLanding renders a "Log In" button for exactly that visitor.
 * Gating them would close the only route into the membership: a prospect could
 * not reach the page that signs them up.
 */
function RequireMember({ children }) {
  const { token, section } = useParams();
  const [params] = useSearchParams();
  const isPublicDoorway = params.has("gid") || params.has("ref");

  if (!hasSession(token) && !isPublicDoorway) {
    const qs = new URLSearchParams();
    if (section) qs.set("next", section);
    const ref = params.get("ref");
    if (ref) qs.set("ref", ref);
    return <Navigate to={`/membership/${token}/login?${qs}`} replace />;
  }
  return children;
}

/**
 * Guard for /membership/:token/:section.
 *
 * `:section` matches any string, so a typo'd or stale slug used to reach
 * MembershipHome, miss every branch of its render chain, and land on the
 * community group list — a wrong URL that rendered as a real page. Anything that
 * isn't a section we actually have now gets a 404 instead.
 *
 * 404 is decided BEFORE the sign-in check: a URL that does not exist should say
 * so rather than sending someone to log in for a page that will 404 afterwards.
 *
 * Both checks live here rather than inside MembershipHome so they run before
 * that component's hooks, and so a rejected URL never starts its data fetches.
 */
function MembershipSection() {
  const { token, section } = useParams();
  if (!SECTION_SLUGS.includes(section)) {
    return <NotFound token={token} section={section} />;
  }
  return (
    <RequireMember>
      <MembershipHome />
    </RequireMember>
  );
}

function App() {
  return (
    <Router>
      {/* Locks page scroll whenever any popup/overlay is open (background stays
          put; the popup's own content still scrolls). */}
      <ScrollLockManager />
      {/* Global toast notifications — top-right on desktop, bottom on tablet/phone */}
      <ToastContainer
        /* top-right on laptop/desktop; toast-theme.css moves the container to
           the BOTTOM below 1024px, where a thumb can reach it. Toastify only
           emits the --top-right class, so the move is CSS, not a width branch
           here — one source of truth, and no re-render on resize. */
        position="top-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="light"
        transition={ToastMotion}
        /* A burst of failures used to stack a column of cards down the whole
           viewport — and on a phone, over the entire screen. Three at a time;
           the rest queue and appear as those clear. */
        limit={3}
      />
      <Routes>
        {/* Membership login entry from the backend admin's Visit button — a
            dedicated, brand-themed single login page (not the group collection
            with a popup). On success it continues into the community. */}
        <Route path="/membership/:token/login" element={<MembershipLogin />} />
        {/* Admin "Sign in as instructor" landing. The :code is single-use and
            expires in a minute — redeemed on arrival, never stored. */}
        <Route path="/membership/:token/sso/:code" element={<MembershipSsoLogin />} />
        {/* Public course review form — the creator shares this link outside the
            platform, so it takes no session. :code is the course's own page code,
            never a numeric id. Declared above the /:section catch-all it can
            never collide with (four segments to its three), so the order is for
            the reader, not the router. */}
        <Route path="/membership/:token/cr/:code" element={<CourseReviewForm />} />
        {/* Password reset target of the emailed link:
            /resetpassword?id=<encrypted funnel_id>&token=<reset token> */}
        <Route path="/resetpassword" element={<ResetPassword />} />
        {/* Public course landing (sales) page — /cs/:code. Renders the chosen
            template (1-5) from the admin's course-landing settings. No auth. */}
        <Route path="/cs/:code" element={<CourseLandingPage />} />
        {/* Public course CHECKOUT page — /cs/:code/checkout. Renders the matching
            CheckoutTemplate (1-17) from the admin's checkout designer. No auth. */}
        <Route path="/cs/:code/checkout" element={<CheckoutPage />} />
        {/* Post-purchase thank-you + credentials — /cs/:code/thankyou?order=<token>. */}
        <Route path="/cs/:code/thankyou" element={<ThankYouPage />} />
        <Route path="/thankyou-preview/:id" element={<ThankYouPreview />} />
        {/* Static thumbnail/preview harness for the 17 checkout templates —
            /checkout-preview/:id. Renders CheckoutTemplate{id} with mock data. */}
        <Route path="/checkout-preview/:id" element={<CheckoutPreview />} />
        {/* Section-slug routes: /membership/:token/:section
            (courses | feed | groups | events | members | map | leaderboard |
            rewards). MembershipHome renders the matching section; group-scoped
            sections open the member's implicit (primary/active) group. */}
        <Route path="/membership/:token/:section" element={<MembershipSection />} />
        <Route path="/membership/:token" element={<MembershipTokenRedirect />} />
        {/* Backward-compatible community page + public group landing
            (/:token and /:token?gid=…). Signed-out visitors are sent to the login
            page, except on the ?gid / ?ref landing that exists for them. */}
        <Route
          path="/:token"
          element={
            <RequireMember>
              <MembershipHome />
            </RequireMember>
          }
        />
        <Route path="*" element={<NoCommunity />} />
      </Routes>
    </Router>
  );
}

// /membership/:token (no section) → the membership's landing page. That is the
// Home dashboard when the creator switched it on, else Courses — not known
// until the membership loads, so this goes to /home and MembershipHome forwards
// to Courses when Home is off. Never the group list: this app is
// membership-first, unlike the group-first community app it came from.
function MembershipTokenRedirect() {
  const { token } = useParams();
  return <Navigate to={`/membership/${token}/${START_PAGE}`} replace />;
}

function NoCommunity() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 text-center text-sm text-gray-500">
      Add your community to the URL — e.g. <span className="font-medium ml-1">/your-community</span>.
    </div>
  );
}

export default App;
